import { Modal } from 'components'
import { ReactElement, ReactNode, useState, useEffect, useRef } from 'react'
import { Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { useBlocker, useNavigate } from 'react-router-dom'

interface Props {
  onConfirm(): void
  onCancel?(): void
  children?: ReactNode
}

const modalDescription = {
  fontSize: 22,
  fontWeight: 'bold',
  maxWidth: 380,
  color: (theme: Theme) => theme.palette.text.secondary,
}

export const useConfirmationModal = ({
  onConfirm,
  onCancel,
  children,
}: Props): [ReactElement, VoidFunction, VoidFunction] => {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const handleConfirm = () => {
    onConfirm()
    setIsOpen(false)
  }

  const handleCancel = () => {
    onCancel?.()
    setIsOpen(false)
  }

  return [
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Attention!"
      onConfirm={handleConfirm}
      onConfirmButtonText="Leave"
    >
      <Typography sx={modalDescription}>{children}</Typography>
    </Modal>,
    () => setIsOpen(true),
    () => setIsOpen(false),
  ]
}

export const useUnsavedChangesWarning = (hasUnsavedChanges: boolean) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const hasHandledBlockerRef = useRef(false)

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  )

  const handleConfirm = () => {
    hasHandledBlockerRef.current = true
    setIsModalOpen(false)
    blocker.proceed?.()
  }

  const handleCancel = () => {
    hasHandledBlockerRef.current = true
    setIsModalOpen(false)
    blocker.reset?.()
  }

  useEffect(() => {
    if (blocker.state === 'blocked' && !hasHandledBlockerRef.current) {
      setIsModalOpen(true)
    }

    if (blocker.state !== 'blocked') {
      hasHandledBlockerRef.current = false
    }
  }, [blocker.state])

  useEffect(() => {
    if (!hasUnsavedChanges) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [hasUnsavedChanges])

  const confirmModal = (
    <Modal
      isOpen={isModalOpen}
      onClose={handleCancel}
      title="Attention!"
      onConfirm={handleConfirm}
      onConfirmButtonText="Leave"
    >
      <Typography sx={modalDescription}>
        You have unsaved changes. Are you sure you want to leave this page? Your
        changes will be lost.
      </Typography>
    </Modal>
  )

  return { confirmModal }
}
